import type {
  ActiveCustomerSizeProfileQuery,
  SizeProfileFitIssue,
  SizeProfileFitRating,
  SizeProfileMeasurementsInput,
  SizeProfileUsualSizesInput,
  SizeProfileVariantFragment,
} from '#graphql-operations'

// Named apart from the schema's own `SizeProfile*` types, which the generated operations also export.
export type SizeProfileData = NonNullable<ActiveCustomerSizeProfileQuery['activeCustomerSizeProfile']>
export type SizeProfilePurchase = SizeProfileData['purchasedItems'][number]
export type SizeProfileWardrobeEntry = SizeProfileData['wardrobe'][number]
export type SizeProfileOwnedEntry = SizeProfileData['ownedItems'][number]
export type SizeProfileMeasurementValues = NonNullable<SizeProfileData['measurements']>
export type SizeProfileUsualSizeValues = NonNullable<SizeProfileData['usualSizes']>
type VariantId = SizeProfileVariantFragment['id']

/** A whole fit answer: each one replaces the last, so an issue or a note left out is cleared. */
export interface SizeProfileFitAnswer {
  rating: SizeProfileFitRating | null
  /** What is wrong, with `FITS_POORLY` only. */
  issue?: SizeProfileFitIssue | null
  /** The shopper's own words, with the `OTHER` issue only. */
  note?: string | null
}

/** IDs arrive as strings but are typed `string | number`; compared as strings so neither slips through. */
const sameId = (a: VariantId, b: VariantId) => String(a) === String(b)

/** `useGraphqlMutation` answers a GraphQL error with `errors`, not by throwing, so it is made to throw here. */
function throwOnErrors(errors: readonly { message?: string }[] | undefined) {
  if (errors?.length)
    throw new Error(errors[0]?.message || 'Request failed')
}

/**
 * The signed-in customer's size profile, shared by the account's "Your sizes" pages.
 *
 * Keyed on the customer id, never on a "loaded" flag: the profile is personal, and a latch that
 * outlived a sign-out would hand it to the next person on the device. The account area renders on
 * the client only, so this never runs during SSR.
 */
export function useSizeProfile() {
  const auth = useAuth()
  const customerId = computed(() => auth?.activeCustomer.value?.id)

  const { data: profile, status, refresh } = useAsyncData<SizeProfileData | null>(
    () => `size-profile:${customerId.value ?? 'none'}`,
    async () => {
      if (!customerId.value)
        return null
      const { data, errors } = await useGraphqlQuery('activeCustomerSizeProfile')
      throwOnErrors(errors)
      return data.activeCustomerSizeProfile ?? null
    },
    // The key follows the customer, so signing in as someone else refetches on its own. Deep,
    // because every change below edits the profile in place — a rating, a wardrobe piece — and
    // Nuxt's shallow default would let those edits pass without a re-render.
    { server: false, deep: true },
  )

  async function saveMeasurements(input: SizeProfileMeasurementsInput) {
    const { data, errors } = await useGraphqlMutation('updateActiveCustomerMeasurements', { input })
    throwOnErrors(errors)
    if (profile.value)
      profile.value.measurements = data.updateActiveCustomerMeasurements
    return data.updateActiveCustomerMeasurements
  }

  async function saveUsualSizes(input: SizeProfileUsualSizesInput) {
    const { data, errors } = await useGraphqlMutation('updateActiveCustomerUsualSizes', { input })
    throwOnErrors(errors)
    if (profile.value)
      profile.value.usualSizes = data.updateActiveCustomerUsualSizes
    return data.updateActiveCustomerUsualSizes
  }

  /**
   * Answers how a received size fits, shown at once rather than after the round trip — it moves
   * between "to rate" and "rated" straight away; a failure puts the previous answer back before it
   * rethrows.
   */
  async function setFit(productVariantId: VariantId, answer: SizeProfileFitAnswer) {
    const item = profile.value?.purchasedItems.find(entry => sameId(entry.productVariant.id, productVariantId))
    const previous = item && { rating: item.rating, issue: item.issue, note: item.note, ratedAt: item.ratedAt }
    // The same piece in "Můj šatník" shows the fit on its badge, so it follows the answer too.
    const owned = profile.value?.ownedItems.find(entry => sameId(entry.productVariant.id, productVariantId))
    const previousOwnedRating = owned?.rating
    if (owned)
      owned.rating = answer.rating ?? undefined
    const issue = answer.issue ?? null
    const note = answer.note?.trim() || null
    // The note is not shown anywhere but its own field, so it waits for the server: written ahead
    // and put back after a failure, it would wipe the field's unsaved text on the way.
    if (item)
      Object.assign(item, { rating: answer.rating ?? undefined, issue: issue ?? undefined })
    try {
      const { data, errors } = await useGraphqlMutation('setPurchasedItemFit', { productVariantId, rating: answer.rating, issue, note })
      throwOnErrors(errors)
      if (item)
        Object.assign(item, data.setPurchasedItemFit)
      if (owned)
        owned.rating = data.setPurchasedItemFit.rating
    }
    catch (error) {
      if (item && previous)
        Object.assign(item, previous)
      if (owned)
        owned.rating = previousOwnedRating
      throw error
    }
  }

  /** `setFit` for a piece added by hand ("Moje reference"): the same answers, the same rollback. */
  async function setWardrobeFit(productVariantId: VariantId, answer: SizeProfileFitAnswer) {
    const item = profile.value?.wardrobe.find(entry => sameId(entry.productVariant.id, productVariantId))
    const previous = item && { rating: item.rating, issue: item.issue, note: item.note, ratedAt: item.ratedAt }
    const issue = answer.issue ?? null
    const note = answer.note?.trim() || null
    if (item)
      Object.assign(item, { rating: answer.rating ?? undefined, issue: issue ?? undefined })
    try {
      const { data, errors } = await useGraphqlMutation('setWardrobeItemFit', { productVariantId, rating: answer.rating, issue, note })
      throwOnErrors(errors)
      if (item)
        Object.assign(item, data.setWardrobeItemFit)
    }
    catch (error) {
      if (item && previous)
        Object.assign(item, previous)
      throw error
    }
  }

  async function addToWardrobe(productVariantId: VariantId) {
    const { data, errors } = await useGraphqlMutation('addWardrobeItem', { productVariantId })
    throwOnErrors(errors)
    const added = data.addWardrobeItem
    if (profile.value && !profile.value.wardrobe.some(entry => sameId(entry.productVariant.id, added.productVariant.id)))
      profile.value.wardrobe.unshift(added)
    return added
  }

  async function removeFromWardrobe(productVariantId: VariantId) {
    const { errors } = await useGraphqlMutation('removeWardrobeItem', { productVariantId })
    throwOnErrors(errors)
    if (profile.value)
      profile.value.wardrobe = profile.value.wardrobe.filter(entry => !sameId(entry.productVariant.id, productVariantId))
  }

  return {
    profile,
    status,
    refresh,
    saveMeasurements,
    saveUsualSizes,
    setFit,
    setWardrobeFit,
    addToWardrobe,
    removeFromWardrobe,
  }
}
